import { BillingEntityTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { DEFAULT_CURRENCY } from '@app/common/constants';

import { getDatePickerLocaleByLanguage, getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { APP_PREFIX, VISITS_PAGE } from '@app/routes/urls';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { useTranslation } from 'react-i18next';
import { FiUsers } from 'react-icons/fi';
import { LuStethoscope } from 'react-icons/lu';
import { MdOutlinePerson, MdOutlinePointOfSale } from 'react-icons/md';
import { RiNurseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

import UpdateConductedPaymentModal from '../components/UpdateConductedPaymentModal/UpdateConductedPaymentModal';
import CustomerProfileFilters from './CustomerProfileFilters';
import CustomerProfileLedgerTable, { LedgerRow } from './CustomerProfileLedgerTable';
import { type EntityType, useCustomerProfile } from './useCustomerProfile';
import { useExportCustomerProfile } from './useExportCustomerProfile';

const CustomerProfile = () => {
  const { t, i18n: localization } = useTranslation();
  const navigate = useNavigate();

  // THIS IS HUGE NEEDS TO BREAK DOWN -- FOR NOW ITS OK TILL WE HAVE EVERYTHING FROM BUSINESS AND BE
  const {
    entityType,
    entityIdentifier,
    startingDate,
    endingDate,
    ledgerRows,
    paymentsResponse,
    currentBalanceTotal,
    paginationLimit,
    editingPaymentId,
    editingEntityIdentifier,
    selectedClient,
    selectedDoctor,
    selectedCaregiver,
    changeEntityType,
    changeEntityIdentifier,
    changeStartingDate,
    changeEndingDate,
    setPaginationOffset,
    setPaginationLimit,
    openEditModal,
    closeEditModal,
    deletePaymentById,
    isLoadingPayments,
  } = useCustomerProfile();

  const { exportCustomerProfile } = useExportCustomerProfile();

  const locale = getNumberFormattingLocaleByLanguage(localization.language);
  const datePickerLocale = getDatePickerLocaleByLanguage(localization.language);
  const dateFormat = t('date-format');

  const typeOptions: Array<{ value: EntityType; label: string }> = [
    { value: BillingEntityTypeEnum.Client, label: t('clients') },
    { value: BillingEntityTypeEnum.Doctor, label: t('doctors') },
    { value: BillingEntityTypeEnum.Caregiver, label: t('caregivers') },
    { value: BillingEntityTypeEnum.CaregiverCashRegister, label: t('caregiver-cash-register') },
  ];

  const balanceColorClass =
    currentBalanceTotal > 0 ? 'text-emerald-600' : currentBalanceTotal < 0 ? 'text-rose-500' : 'text-slate-400';

  const iconByEntityType: Partial<Record<BillingEntityTypeEnum, typeof MdOutlinePerson>> = {
    [BillingEntityTypeEnum.Client]: MdOutlinePerson,
    [BillingEntityTypeEnum.Doctor]: LuStethoscope,
    [BillingEntityTypeEnum.Caregiver]: RiNurseFill,
    [BillingEntityTypeEnum.CaregiverCashRegister]: MdOutlinePointOfSale,
  };

  const colorByEntityType: Partial<Record<BillingEntityTypeEnum, string>> = {
    [BillingEntityTypeEnum.Client]: '#16a34a',
    [BillingEntityTypeEnum.Doctor]: '#94a3b8',
    [BillingEntityTypeEnum.Caregiver]: '#7dd3fc',
    [BillingEntityTypeEnum.CaregiverCashRegister]: '#f59e0b',
  };

  const EntityIcon = iconByEntityType[entityType] ?? iconByEntityType[BillingEntityTypeEnum.Client]!;
  const entityColor = colorByEntityType[entityType] ?? colorByEntityType[BillingEntityTypeEnum.Client]!;
  const entityTint = `${entityColor}1a`;
  const formattedCurrentBalanceTotal =
    currentBalanceTotal === 0
      ? currencyFormatter(0, DEFAULT_CURRENCY, locale)
      : `${currentBalanceTotal > 0 ? '+' : '-'}${currencyFormatter(Math.abs(currentBalanceTotal), DEFAULT_CURRENCY, locale)}`;

  const handleEdit = (row: LedgerRow) => {
    openEditModal(row.paymentId, row.entityIdentifier);
  };

  const handleDelete = (row: LedgerRow) => {
    deletePaymentById(row.paymentId);
  };

  const handleVisitClick = (visitId: number) => {
    navigate(`/${APP_PREFIX}/${VISITS_PAGE}/${visitId}/edit`);
  };

  const ledgerEmptyText = !entityIdentifier ? (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-slate-100 text-slate-600">
        <FiUsers size={18} />
      </span>
      <p className="text-base font-semibold tracking-wide text-slate-700">{t('select-entity-first')}</p>
    </div>
  ) : undefined;

  return (
    <div className="h-[calc(100vh-100px)] overflow-hidden bg-slate-50">
      <div className="flex h-full w-full flex-col">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="w-fit rounded-lg border border-slate-200 bg-white px-5 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-3">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full border"
                  style={{
                    borderColor: entityColor,
                    backgroundColor: entityTint,
                    color: entityColor,
                  }}
                >
                  <EntityIcon size={12} />
                </span>
                <span className="text-lg font-semibold text-slate-900">{t('total')}</span>
              </div>
              <div className="flex items-baseline gap-1 text-xl font-semibold">
                <span className="text-slate-400">:</span>
                <span className={balanceColorClass}>{formattedCurrentBalanceTotal}</span>
              </div>
            </div>
          </div>

          <CustomerProfileFilters
            entityTypeValue={entityType}
            entityTypeOptions={typeOptions}
            entityIdentifierValue={entityIdentifier}
            startingDateValue={startingDate}
            endingDateValue={endingDate}
            dateFormat={dateFormat}
            datePickerLocale={datePickerLocale}
            onEntityTypeChange={changeEntityType}
            onEntityIdentifierChange={changeEntityIdentifier}
            onStartingDateChange={changeStartingDate}
            onEndingDateChange={changeEndingDate}
            selectPlaceholder={t('select')}
            startingDatePlaceholder={t('from')}
            endingDatePlaceholder={t('to')}
            exportLabel={t('export')}
            onExport={() =>
              exportCustomerProfile({
                entityType,
                entityId: entityIdentifier,
                startAt: startingDate,
                endAt: endingDate,
              })
            }
            selectedClient={selectedClient}
            selectedDoctor={selectedDoctor}
            selectedCaregiver={selectedCaregiver}
          />
        </div>

        <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <CustomerProfileLedgerTable
            entityType={entityType}
            rows={ledgerRows}
            currency={DEFAULT_CURRENCY}
            locale={locale}
            emptyText={ledgerEmptyText}
            isLoading={isLoadingPayments}
            totalCount={paymentsResponse.total}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onVisitClick={handleVisitClick}
            onPageChange={(page, pageSize) => {
              setPaginationOffset(page * pageSize);
              if (pageSize !== paginationLimit) {
                setPaginationLimit(pageSize);
              }
            }}
          />
        </div>
      </div>

      <UpdateConductedPaymentModal
        paymentId={editingPaymentId}
        entityIdentifier={editingEntityIdentifier}
        onClose={closeEditModal}
      />
    </div>
  );
};

export default CustomerProfile;
